import React, { lazy, Suspense } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ErrorBoundary from "./utils/ErrorBoundary";
import ProtectedRoute from "./utils/ProtectedRoute";
import OnboardingGuard from "./utils/OnboardingGuard";
import RoleGuard from "./utils/RoleGuard";
import { getAuthRole } from "./utils/authHelpers";
import DashboardLayout from "./components/DashboardLayout/DashboardLayout";
import { Login } from "./pages";
import "./App.css";

/* ---------------- Lazy Imports ---------------- */

const Modules = lazy(()=> import("./components/dashboard/Modules"))
const SubModules = lazy(()=> import("./components/dashboard/SubModule"))
const Plans = lazy(()=> import("./components/dashboard/Plans"))
const PlanName = lazy(()=> import("./components/dashboard/PlanName"))
const PlansDetails = lazy(()=> import("./components/dashboard/Plansdetails"))
const Features = lazy(()=> import("./components/dashboard/Features"))
const Subscription = lazy(()=> import("./components/dashboard/subscription/Subscription"))
const AdminSubscription = lazy(() => import("./components/dashboard/subscription/AdminSubscription"))
const MyPlan = lazy(() => import("./components/dashboard/subscription/MyPlan"))
const AgreementView = lazy(() => import("./components/dashboard/agreement/AgreementView"))

const ManageSubscriptionsView = () => {
  const role = getAuthRole();
  return role === "Admin" ? <AdminSubscription /> : <Subscription />;
};
const SiteData = lazy(()=> import("./components/dashboard/SiteData"))
const ClientSiteData = lazy(()=> import("./components/dashboard/ClientSiteData"))
const OnboardingWizard = lazy(() => import("./pages/onboarding/OnboardingWizard"))
const Roles = lazy(()=> import("./components/dashboard/Roles"))
const Users = lazy(()=> import("./components/dashboard/Users"))
const CreateTemplate = lazy(() => import("./components/dashboard/CreateTemplate"))
const TemplateLibrary = lazy(() => import("./components/dashboard/TemplateLibrary"))
const Industries = lazy(() => import("./components/dashboard/Industries"))
// const Leads = lazy(() => import("./components/dashboard/Leads"));
const Enquiry = lazy(()=> import("./components/dashboard/Enquires"))
const AddEnquiry = lazy(()=> import("./components/dashboard/AddEnquiry"))
const LeadsDetails = lazy(() => import("./components/dashboard/LeadsDetails"));
const Languages = lazy(() => import("./components/dashboard/Languages"));
const Preferedzone = lazy(() => import("./components/dashboard/Preferedzone"));
const Status = lazy(() => import("./components/dashboard/Status"));
const Googlemapkey = lazy(() => import("./components/dashboard/Googlemapkey"));
const Availableshift = lazy(() =>
  import("./components/dashboard/Availableshift")
);
const DaysAvailable = lazy(() =>
  import("./components/dashboard/DaysAvailable")
);
const EducationLevel = lazy(() =>
  import("./components/dashboard/EducationLevel")
);
const States = lazy(() => import("./components/dashboard/States"));
const Districts = lazy(() => import("./components/dashboard/Districts"));
const Cities = lazy(() => import("./components/dashboard/Cities"));
const Coordinators = lazy(() => import("./components/dashboard/Coordinators"));
const LeadSource = lazy(() => import("./components/dashboard/LeadSource"));
const InteractionType = lazy(() =>
  import("./components/dashboard/InteractionType")
);
const InteractionChannel = lazy(() =>
  import("./components/dashboard/InteractionChannel")
);
const InteractionOutcome = lazy(() =>
  import("./components/dashboard/InteractionOutcome")
);
const FollowUpMode = lazy(() => import("./components/dashboard/FollowUpMode"));
const ResourceType = lazy(() => import("./components/dashboard/ResourceType"));
const AssessmentType = lazy(() =>
  import("./components/dashboard/AssessmentType")
);
const AssessmentMode = lazy(() =>
  import("./components/dashboard/AssessmentMode")
);
const RiskCategory = lazy(() => import("./components/dashboard/RiskCategory"));
const ResourceCategory = lazy(() =>
  import("./components/dashboard/ResourceCategory")
);
const Relations = lazy(() => import("./components/dashboard/Relations"));
const Tariffs = lazy(() => import("./components/dashboard/Tariffs"));
const HospitalFacility = lazy(() =>
  import("./components/dashboard/HospitalFacility")
);
const FacilityService = lazy(() =>
  import("./components/dashboard/FacilityService")
);
const Hospitals = lazy(() => import("./components/dashboard/Hospitals"));
const HospitalDetails = lazy(() =>
  import("./components/dashboard/HospitalDetails")
);
const Carepreneur = lazy(() => import("./components/dashboard/Carepreneur"));
const CarepreneurDetails = lazy(() =>
  import("./components/dashboard/CarepreneurDetails")
);
const Blogposts = lazy(() =>
  import("./components/dashboard/Marketing/Blogposts")
);
const BlogCategory = lazy(() =>
  import("./components/dashboard/Marketing/BlogCategory")
);
const Contacts = lazy(() =>
  import("./components/dashboard/Marketing/Contacts")
);
const SeoUrl = lazy(() => import("./components/dashboard/SeoUrl"));
const PaymentSuccess = lazy(() => import("./pages/onboarding/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/onboarding/PaymentCancel"));
const PlanLimits           = lazy(() => import("./components/dashboard/subscription/PlanLimits"));
const Addons               = lazy(() => import("./components/dashboard/subscription/Addons"));
const AddonResourceExtras  = lazy(() => import("./components/dashboard/subscription/AddonResourceExtras"));
const TenantAddons         = lazy(() => import("./components/dashboard/subscription/TenantAddons"));
const TenantUsageInspector = lazy(() => import("./components/dashboard/subscription/TenantUsageInspector"));
/* ---------------- Loader ---------------- */

const PageLoader = () => (
  <div style={{ padding: 40, textAlign: "center" }}>Loading page...</div>
);

/* ---------------- Theme ---------------- */

const theme = createTheme({
  palette: {
    primary: { main: "#f59e0b" },
    background: { default: "#F3F4F6" },
  },
  typography: {
    fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  const role = getAuthRole();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login />} />

              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<DashboardLayout />}>
                  {/* <Route
                    index
                    element={
                      role === "SuperAdmin" ? (
                        <Navigate to="/worklist/myworklist" />
                      ) : null
                    }
                  /> */}

                  <Route
                    path="/onboarding"
                    element={
                      <OnboardingGuard>
                        <OnboardingWizard />
                      </OnboardingGuard>
                    }
                  />
                  
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />

                  {/* Settings - Admin & Client Allowed */}
                  <Route element={<RoleGuard allowedRoles={["SuperAdmin", "Admin", "Client"]} />}>
                    <Route path="/my-plan" element={<MyPlan />} />
                    <Route path="/settings/my-plan" element={<MyPlan />} />
                    <Route path="/agreement" element={<AgreementView />} />
                    <Route path="/settings/agreement" element={<AgreementView />} />
                    <Route path="/settings/sitedata" element={<SiteData />} />
                  </Route>

                  {/* Settings - SuperAdmin Only */}
                  <Route element={<RoleGuard allowedRoles={["SuperAdmin"]} />}>
                    <Route path="/settings/modules" element={<Modules />} />
                    <Route path="/settings/submodules" element={<SubModules />} />
                    <Route path="/settings/plans" element={<Plans />} />
                    <Route path="/settings/plans/add" element={<PlansDetails />} />
                    <Route path="/settings/plans/:id" element={<PlansDetails />} />
                    <Route path="/settings/plans/edit/:id" element={<PlansDetails />} />
                    <Route path="/settings/planname" element={<PlanName />} />
                    <Route path="/settings/features" element={<Features />} />
                    <Route path="/settings/roles" element={<Roles />} />
                    <Route path="/settings/users" element={<Users />} />
                    <Route path="/settings/template-library" element={<TemplateLibrary />} />
                    <Route path="/settings/industries" element={<Industries />} />
                    
                    <Route
                      path="/settings/prefered-zone"
                      element={<Preferedzone />}
                    />
                    <Route path="/settings/status" element={<Status />} />
                    <Route
                      path="/settings/googlemapkey"
                      element={<Googlemapkey />}
                    />
                    <Route
                      path="/settings/availble-shifts"
                      element={<Availableshift />}
                    />
                    <Route
                      path="/settings/days-available"
                      element={<DaysAvailable />}
                    />
                    <Route
                      path="/settings/education-levels"
                      element={<EducationLevel />}
                    />
                    <Route
                      path="/settings/hospital-facilities"
                      element={<HospitalFacility />}
                    />
                    <Route path="/settings/states" element={<States />} />
                    <Route path="/settings/districts" element={<Districts />} />
                    <Route path="/settings/cities" element={<Cities />} />
                    <Route
                      path="/settings/co-ordinator"
                      element={<Coordinators />}
                    />
                    <Route
                      path="/settings/lead-source"
                      element={<LeadSource />}
                    />
                    <Route
                      path="/settings/facility-service"
                      element={<FacilityService />}
                    />
                    <Route
                      path="/settings/interaction-type"
                      element={<InteractionType />}
                    />
                    <Route
                      path="/settings/interaction-channel"
                      element={<InteractionChannel />}
                    />
                    <Route
                      path="/settings/interaction-outcome"
                      element={<InteractionOutcome />}
                    />
                    <Route
                      path="/settings/followupmode"
                      element={<FollowUpMode />}
                    />
                    <Route
                      path="/settings/resourcetype"
                      element={<ResourceType />}
                    />
                    <Route
                      path="/settings/assessment-type"
                      element={<AssessmentType />}
                    />
                    <Route
                      path="/settings/assessment-mode"
                      element={<AssessmentMode />}
                    />
                    <Route
                      path="/settings/risk-category"
                      element={<RiskCategory />}
                    />
                    <Route
                      path="/settings/resource-category"
                      element={<ResourceCategory />}
                    />
                    <Route path="/settings/relations" element={<Relations />} />
                    <Route path="/settings/tariffs" element={<Tariffs />} />

                    {/* Worklist */}
                    {/* <Route path="/worklist/myworklist" element={<Leads />} /> */}
                    <Route path="/worklist/enquires" element={<Enquiry />} />
                    <Route path="/worklist/enquires/add" element={<AddEnquiry />} />
                    <Route path="/worklist/enquires/:id" element={<AddEnquiry />} />
                    <Route path="/worklist/clientonboarding" element={<ClientSiteData />} />
                    <Route path="/worklist/clientonboarding/edit/:id" element={<SiteData />} />
                    {/* <Route
                      path="/worklist/myworklist/add"
                      element={<LeadsDetails />}
                    />
                    <Route
                      path="/worklist/myworklist/:id"
                      element={<LeadsDetails />}
                    /> */}

                    <Route
                      path="/worklist/carepreneur"
                      element={<Carepreneur />}
                    />
                    {/* <Route
                      path="/worklist/carepreneur/add"
                      element={<CarepreneurDetails />}
                    />
                    <Route
                      path="/worklist/carepreneur/:id"
                      element={<CarepreneurDetails />}
                    />

                    <Route path="/worklist/hospital" element={<Hospitals />} />
                    <Route
                      path="/worklist/hospital/add"
                      element={<HospitalDetails />}
                    />
                    <Route
                      path="/worklist/hospital/:id"
                      element={<HospitalDetails />}
                    /> */}

                    {/* Marketing */}
                    <Route
                      path="/marketing/blog-categories"
                      element={<BlogCategory />}
                    />
                    <Route path="/marketing/blog-posts" element={<Blogposts />} />
                    <Route path="/marketing/contacts" element={<Contacts />} />
                    <Route path="/marketing/seo-url" element={<SeoUrl />} />

                  </Route>

                  {/* Subscription - SuperAdmin & Admin */}
                  <Route element={<RoleGuard allowedRoles={["SuperAdmin", "Admin"]} />}>
                    <Route path="/subscription" element={<Navigate to="/subscription/manage-subscriptions" replace />} />
                    <Route path="/subscription/manage-subscriptions" element={<ManageSubscriptionsView />} />
                    <Route path="/subscription/manage-subscriptions/:id" element={<Subscription />} /> 
                  </Route>

                  {/* SuperAdmin only - advanced subscription features */}
                  <Route element={<RoleGuard allowedRoles={["SuperAdmin"]} />}>
                    <Route path="/subscription/plan-limits" element={<PlanLimits />} />
                    <Route path="/subscription/addons" element={<Addons />} />
                    <Route path="/subscription/addon-resource-extras" element={<AddonResourceExtras />} />
                    <Route path="/subscription/tenant-addons" element={<TenantAddons />} />
                    <Route path="/subscription/tenant-usage" element={<TenantUsageInspector />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    </ThemeProvider>
  );
}

export default App;
